import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { AuthShell } from './AuthShell'
import { Button, TextField } from '@/components/layout/ui'
import {
  authLinkClass,
  Checkbox,
  emailProblem,
  FormError,
  PasswordField,
  signInErrorMessage,
} from '@/features/auth'
import { cn } from '@/lib/cn'
import { useLogin } from '@/lib/session'

/**
 * Sign in, in two steps: the address, then the password.
 *
 * This is the Atlassian shape, with one deliberate difference. Jira's first
 * step asks the server whether the address exists and branches on the answer,
 * which turns the sign-in form into an account-enumeration oracle. Taskly's
 * first step talks to nobody: it checks that what was typed looks like an
 * email, and advances. Every address reaches the password screen, and the only
 * thing the server is ever asked is "are these two right together" — answered
 * with one sentence that names neither field.
 *
 * The address is still worth its own step. It is what a password manager keys
 * on, it is what the "wrong account" case turns on, and it lets the second
 * screen be about one thing.
 */
export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()

  const [step, setStep] = useState<'email' | 'password'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [emailError, setEmailError] = useState<string | undefined>(undefined)
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined)

  // Where the guard wanted to send them, carried through the sign-in.
  const from = (location.state as { from?: string } | null)?.from

  function goBackToEmail() {
    login.reset()
    setPassword('')
    setPasswordError(undefined)
    setStep('email')
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (step === 'email') {
      const problem = emailProblem(email)
      setEmailError(problem)
      if (problem) return
      // No lookup, no request: every address advances.
      login.reset()
      setStep('password')
      return
    }

    if (!password) {
      setPasswordError('Enter your password.')
      return
    }
    setPasswordError(undefined)
    login.mutate(
      { email: email.trim(), password, remember },
      { onSuccess: () => navigate(from ?? '/', { replace: true }) },
    )
  }

  const formError = step === 'password' ? signInErrorMessage(login.error) : null

  return (
    <AuthShell
      title="Log in to Taskly"
      description={
        step === 'email'
          ? 'Taskly on the web uses its own account, separate from the one on the iPad app.'
          : undefined
      }
      footer={
        <>
          New to Taskly on the web?{' '}
          <Link to="/register" state={from ? { from } : undefined} className={authLinkClass}>
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {step === 'email' ? (
          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            // Remounted on every return to this step, so it takes focus again.
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) setEmailError(undefined)
            }}
            error={emailError}
          />
        ) : (
          <>
            <IdentityRow email={email.trim()} onChange={goBackToEmail} busy={login.isPending} />

            {/*
              The address stays in the form, hidden, so a password manager can
              still see the pair it is being asked to save. It is out of the tab
              order and out of the accessibility tree — the visible row above
              already says who is signing in.
            */}
            <input
              type="text"
              name="email"
              value={email.trim()}
              autoComplete="username"
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              className="sr-only"
            />

            <PasswordField
              label="Password"
              name="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
                if (passwordError) setPasswordError(undefined)
              }}
              error={passwordError}
            />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Checkbox
                name="remember"
                label="Keep me signed in"
                checked={remember}
                onChange={setRemember}
              />
              <Link
                to={{
                  pathname: '/forgot-password',
                  search: email.trim() ? `?email=${encodeURIComponent(email.trim())}` : '',
                }}
                className={cn('text-sm', authLinkClass)}
              >
                Forgot password?
              </Link>
            </div>
          </>
        )}

        <FormError message={formError} />

        <Button type="submit" variant="primary" className="w-full" loading={login.isPending}>
          {step === 'email' ? 'Continue' : login.isPending ? 'Logging in…' : 'Log in'}
        </Button>
      </form>
    </AuthShell>
  )
}

/**
 * The address being signed in, echoed above the password field.
 *
 * "Change" is a button rather than a link because it changes the state of this
 * form; it is the only way back, so it is a real target with a visible focus
 * ring, not a hint.
 */
function IdentityRow({
  email,
  onChange,
  busy,
}: {
  email: string
  onChange: () => void
  busy: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-50 py-2 pr-2 pl-3 dark:border-ink-800 dark:bg-ink-950">
      <span className="min-w-0 truncate text-sm" title={email}>
        {email}
      </span>
      <button
        type="button"
        onClick={onChange}
        disabled={busy}
        className={cn(
          'shrink-0 rounded-md px-2 py-1 text-sm font-medium text-brand-600 transition',
          'hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950',
          'disabled:pointer-events-none disabled:opacity-55',
        )}
      >
        Change
        <span className="sr-only"> email address</span>
      </button>
    </div>
  )
}
